/** Refresh only public availability; entered details stay in the current form. */
export default class CallbackForm {
    /** Find the form controls and keep entered details in the current page only. */
    constructor(root) {
        this.root = root;
        this.form = root.querySelector('form');
        this.date = root.querySelector('select[name="callback-date"]');
        this.time = root.querySelector('select[name="preferred-time"]');
        this.status = root.querySelector('[data-callback-status]');
        this.countdown = root.querySelector('[data-callback-countdown]');
        this.retry = root.querySelector('[data-callback-retry]');
        this.submit = root.querySelector('[type="submit"]');
        this.days = [];
        this.ready = false;
        this.active = false;
        this.warned = false;
        if (!this.date || !this.time || !this.status) return;
        this.datePrompt = this.date.options[0].text;
        this.timePrompt = this.time.options[0].text;
        // Created here so saved forms get it without restoring the default markup.
        this.submitNote = document.createElement('p');
        this.submitNote.id = 'callback-submit-note';
        this.submitNote.dataset.callbackSubmitNote = '';
        this.submitNote.hidden = true;
        this.submit.before(this.submitNote);
        this.submit.setAttribute('aria-describedby', this.submitNote.id);
        this.date.addEventListener('change', () => {
            this.status.textContent = '';
            this.fillTimes(false);
            this.refresh();
        });
        this.time.addEventListener('change', () => {
            this.status.textContent = '';
            this.warned = false;
            this.tick();
        });
        this.retry.addEventListener('click', () => this.refresh());
        this.form.addEventListener('submit', (event) => {
            this.tick();
            this.updateSchema();
            if (!this.ready) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.refresh();
            }
        }, true);
        // CF7 may finish loading its schema after our first availability request.
        this.form.addEventListener('change', () => this.updateSchema(), true);
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && this.active) this.refresh();
        });
        window.addEventListener('focus', () => {
            if (this.active) this.refresh();
        });
        this.form.addEventListener('reset', () => {
            this.countdown.hidden = true;
            this.warned = false;
            this.status.textContent = '';
            this.time.replaceChildren(new Option(this.timePrompt, ''));
        });
    }

    /** Refresh when the visitor opens the form, then watch for expiring choices. */
    open() {
        if (!this.date || !this.time || !this.status) return;
        this.active = true;
        this.refresh();
        clearInterval(this.timer);
        this.timer = setInterval(() => {
            this.tick();
            if (performance.now() - this.loadedAt >= 60000 && !document.hidden) this.refresh();
        }, 1000);
    }

    /** Stop the clock when the callback view is no longer visible. */
    close() {
        this.active = false;
        clearInterval(this.timer);
    }

    /** Fetch public dates and times without sending any form answers. */
    refresh() {
        if (this.loading) return;
        this.loading = true;
        this.ready = false;
        this.submit.disabled = true;
        this.retry.hidden = true;
        fetch(this.root.dataset.callbackEndpoint, {cache: 'no-store', credentials: 'same-origin'})
        .then(response => {
            if (!response.ok) throw new Error('Availability unavailable');
            return response.json();
        })
        .then(data => {
            if (!Array.isArray(data.days) || !Number.isFinite(data.now)) throw new Error('Invalid availability');
            this.days = data.days;
            this.serverNow = data.now;
            this.loadedAt = performance.now();
            this.root.querySelector('[data-callback-timezone]').textContent = `All times are in ${data.timezone}.`;
            this.replaceOptions(this.date, this.days, this.datePrompt);
            this.fillTimes(true);
            this.ready = data.enabled && this.days.length > 0;
            this.date.disabled = !this.ready;
            this.time.disabled = !this.ready || !this.date.value;
            this.submit.disabled = !this.ready;
            this.root.classList.toggle('is-unavailable', !this.ready);
            this.noteSubmit(this.ready ? '' : 'Callback requests are currently unavailable, so this form cannot be sent. Please try the other contact options.');
            if (!this.ready) {
                this.status.textContent = 'Callback requests are currently unavailable. Please go back and try other contact options.';
            } else if (this.failed) {
                this.status.textContent = 'Available dates and times have been updated. Your details are still here.';
            }
            this.failed = false;
            this.updateSchema();
            this.tick();
        }).catch(() => {
            this.failed = true;
            this.ready = false;
            this.root.classList.remove('is-unavailable');
            this.noteSubmit('Available times could not be loaded, so this form cannot be sent yet. Use "Try loading available times again" above.');
            this.date.disabled = true;
            this.time.disabled = true;
            this.countdown.hidden = true;
            this.retry.hidden = false;
            this.status.textContent = 'We could not load the available times. Please try again. Your details are still here, and you can use the other contact options above.';
        }).finally(() => {
            this.loading = false;
        });
    }

    /** Explain a disabled submit button beside it, since the status panel may be scrolled out of view. */
    noteSubmit(message) {
        this.submitNote.textContent = message;
        this.submitNote.hidden = !message;
    }

    /** Keep a previous choice only while it is still available. */
    replaceOptions(select, options, prompt) {
        const previous = select.value;
        select.replaceChildren(new Option(prompt, ''), ...options.map(option => new Option(option.label, option.value)));
        select.value = options.some(option => option.value === previous) ? previous : '';
        if (previous && !select.value) this.expired();
    }

    /** Show the time windows for the selected date without picking one for the visitor. */
    fillTimes(preserve) {
        const day = this.days.find(day => day.value === this.date.value);
        if (!preserve) this.time.value = '';
        this.replaceOptions(this.time, day ? day.slots : [], this.timePrompt);
        this.time.disabled = !day;
        if (!preserve || !this.time.value) this.warned = false;
        this.countdown.hidden = true;
        this.updateSchema();
    }

    /** Keep CF7 browser validation aligned with the refreshed dropdowns. */
    updateSchema() {
        const schema = this.form.wpcf7 && this.form.wpcf7.schema;
        if (!schema || !schema.rules) return;
        // Keep CF7's browser enum checks in step with refreshed options, including
        // tabs left open overnight. The server independently checks both fields.
        schema.rules.forEach(rule => {
            if (rule.rule !== 'enum') return;
            if (rule.field === 'callback-date') rule.accept = this.days.map(day => day.value);
            if (rule.field === 'preferred-time') rule.accept = Array.from(this.time.options).map(option => option.value).filter(Boolean);
        });
    }

    /** Use the server clock so an incorrect device clock cannot change the warning. */
    tick() {
        if (!this.ready || !this.active) return;
        const day = this.days.find(day => day.value === this.date.value);
        const slot = day && day.slots.find(slot => slot.value === this.time.value);
        this.countdown.hidden = true;
        if (!slot) return;
        const now = this.serverNow + (performance.now() - this.loadedAt) / 1000;
        const seconds = Math.ceil(slot.expires - now);
        if (seconds <= 0) {
            this.time.value = '';
            this.expired();
            this.refresh();
        } else if (seconds <= 300) {
            this.countdown.hidden = false;
            const minutes = Math.floor(seconds / 60);
            const remainder = String(seconds % 60).padStart(2, '0');
            this.countdown.textContent = `You can request this time for another ${minutes}:${remainder}. If it changes, you can choose another time. Your details will stay here.`;
            // Announce once; do not read a ticking clock every second.
            if (!this.warned) {
                this.status.textContent = 'There are fewer than five minutes left to request this time. If it changes, you can choose another time. Your details will stay here.';
                this.warned = true;
            }
        }
    }

    /** Explain a lost choice without clearing the visitor's other answers. */
    expired() {
        this.status.textContent = 'That date or time is no longer available to request. Please choose another. Your other details are still here.';
    }
}
